import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Modal, NumberInput, Progress, Select, TextInput } from '@mantine/core';
import { flushSync } from 'react-dom';
import { notifications } from '@mantine/notifications';
import type { Attendance150Range, AttendanceReportRules, AttendanceShiftView } from 'shared-models/browser';
import { AttendanceDepartmentModal } from './AttendanceDepartmentModal';
import { AttendanceQrModal } from './AttendanceQrModal';
import { AttendanceSummaryModal } from './AttendanceSummaryModal';
import { DEFAULT_ATTENDANCE_REPORT_RULES, DEFAULT_ATTENDANCE_SUMMARY_ROWS } from 'shared-models/browser';
import type { EmployeeIdentity, EmployeeView } from '../../../../services/employees/employees.service';
import { attendanceService } from '../../../../services/attendance/attendance.service';
import type { AttendanceActionType, AttendanceDashboardView } from '../../../../services/attendance/attendance.service';
import type { PrinterView } from '../../../../services/printers/printers.service';
import { captureReceiptSnapshot } from '../receipt/shared/receipt.utils';
import { compareEmployeeNames } from '../auth/employee-sort.utils';
import { PickListModal, type PickListOption } from '../../../../components/PickListModal/PickListModal';
import { Button } from '../../../../components/Button';
import { toHebrewApiErrorMessage } from '../../../../services/index/index.service';
import settingsClasses from './shared/settings-shared.module.scss';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import { applyAttendanceShiftSnapshot, attendancePayableTotal, buildAttendancePayMinutes, resolveAttendanceReportRules, formatOngoingShiftDuration } from './attendance-report.utils';
import { useAttendanceBatchPrint, type AttendanceBatchReport } from './useAttendanceBatchPrint';
import { NoBillWarningModal } from '../receipt/no-bill-warning/NoBillWarningModal';

type AttendanceSettingsModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string;
  employees: Array<EmployeeView | EmployeeIdentity>;
  lockedEmployeeId?: number | null;
  selfServiceMode?: boolean;
  attendanceReportRules?: AttendanceReportRules;
  onSaveReportRules?: (patch: Partial<AttendanceReportRules>) => Promise<boolean>;
};

const toDateIso = (value: string): string => {
  const date = new Date(value);
  return date.toISOString();
};

const defaultRange = (): { from: string; to: string } => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  const toDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: toDate(start), to: toDate(end) };
};

const formatActionType = (actionType: AttendanceActionType): string => (actionType === 'clock_in' ? 'כניסה למשמרת' : 'יציאה ממשמרת');

const formatDurationHmsFromMinutes = (minutes: number): string => {
  const totalMinutes = Math.max(0, Math.round(minutes));
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const formatDateCompact = (iso: string): string => {
  const date = new Date(iso);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const formatRangeDateShort = (value: string): string => {
  const [yyyy, mm, dd] = value.split('-');
  if (!yyyy || !mm || !dd) {
    return value;
  }
  return `${dd}/${mm}/${yyyy.slice(-2)}`;
};

const weekdayLabel = (day: number): string => {
  const labels = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
  return labels[day] ?? String(day);
};

const toDateTimeLocalValue = (iso: string): string => {
  const date = new Date(iso);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const formatTimeCompact = (iso: string | null): string => {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const hebrewWeekday = (iso: string): string => {
  const names = ['יום א׳', 'יום ב׳', 'יום ג׳', 'יום ד׳', 'יום ה׳', 'יום ו׳', 'שבת'];
  return names[new Date(iso).getDay()] ?? '';
};

const formatDateTimeWithWeekday = (iso: string | null): string => {
  if (!iso) {
    return '—';
  }
  return `${hebrewWeekday(iso)}, ${new Date(iso).toLocaleString('he-IL')}`;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;
const ISRAEL_TZ = 'Asia/Jerusalem';
const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const localDateKey = (iso: string): string => {
  const date = new Date(iso);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const ISRAEL_DATE_PARTS_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: ISRAEL_TZ,
  weekday: 'short',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const israelDateParts = (date: Date): { day: number; hour: number; minute: number; dateKey: string } => {
  const parts = ISRAEL_DATE_PARTS_FORMATTER.formatToParts(date);
  const value = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  const day = WEEKDAY_INDEX[value('weekday')] ?? 0;
  const hour = Number(value('hour'));
  const minute = Number(value('minute'));
  const yyyy = value('year');
  const mm = value('month');
  const dd = value('day');
  return {
    day,
    hour: Number.isFinite(hour) ? hour : 0,
    minute: Number.isFinite(minute) ? minute : 0,
    dateKey: `${yyyy}-${mm}-${dd}`,
  };
};

const parseTimeToMinute = (time: string): number => {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) {
    return 0;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return 0;
  }
  return (hours * 60) + minutes;
};

const weekMinute = (date: Date): number => {
  const parts = israelDateParts(date);
  return (parts.day * 1440) + (parts.hour * 60) + parts.minute;
};

const isNightWindowMinute = (date: Date): boolean => {
  const { hour } = israelDateParts(date);
  return hour >= 22 || hour < 6;
};

const isWithinWeeklyRange = (date: Date, range: Attendance150Range): boolean => {
  const start = (range.fromWeekday * 1440) + parseTimeToMinute(range.fromTime);
  const end = (range.toWeekday * 1440) + parseTimeToMinute(range.toTime);
  const point = weekMinute(date);

  if (start === end) {
    return true;
  }
  if (start < end) {
    return point >= start && point < end;
  }
  return point >= start || point < end;
};

const resolveDayBonusRate = (
  minuteDate: Date,
  specialDates: Set<string>,
  range150: Attendance150Range | null,
  weekdays150: Set<number>,
): 0 | 0.5 | 1 => {
  const israelParts = israelDateParts(minuteDate);
  const dateKey = israelParts.dateKey;
  if (specialDates.has(dateKey)) {
    return 1;
  }
  if (range150) {
    return isWithinWeeklyRange(minuteDate, range150) ? 0.5 : 0;
  }
  return weekdays150.has(israelParts.day) ? 0.5 : 0;
};

export const AttendanceSettingsModal: React.FC<AttendanceSettingsModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
  employees,
  lockedEmployeeId = null,
  selfServiceMode = false,
  attendanceReportRules: generalAttendanceReportRules = DEFAULT_ATTENDANCE_REPORT_RULES,
  onSaveReportRules,
}) => {
  type ManualAddMode = 'single_action' | 'full_shift';

  const [range, setRange] = useState(defaultRange);
  const [loadedDashboard, setDashboard] = useState<AttendanceDashboardView | null>(null);
  const [batchReport, setBatchReport] = useState<AttendanceBatchReport | null>(null);
  const dashboard = batchReport?.dashboard ?? loadedDashboard;
  const [loading, setLoading] = useState(false);
  const [isQrOpen, setIsQrOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  useEffect(() => { if (!opened) { setIsQrOpen(false); setIsSummaryOpen(false); } }, [opened]);
  const [saving, setSaving] = useState(false);
  const [manualEmployeeId, setManualEmployeeId] = useState<string | null>(null);
  const [manualAddMode, setManualAddMode] = useState<ManualAddMode>('single_action');
  const [reportEmployeeId, setReportEmployeeId] = useState<string | null>(null);
  const attendanceReportRules = resolveAttendanceReportRules(generalAttendanceReportRules, batchReport?.employee.id ?? (reportEmployeeId ? Number(reportEmployeeId) : lockedEmployeeId));
  const [isMultiEmployeePickerOpen, setIsMultiEmployeePickerOpen] = useState(false);
  const [selectedBatchEmployeeIds, setSelectedBatchEmployeeIds] = useState<number[]>([]);
  const [manualActionType, setManualActionType] = useState<AttendanceActionType>('clock_in');
  const [manualActionDate, setManualActionDate] = useState('');
  const [manualShiftStartDate, setManualShiftStartDate] = useState('');
  const [manualShiftEndDate, setManualShiftEndDate] = useState('');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isActionsHistoryModalOpen, setIsActionsHistoryModalOpen] = useState(false);
  const [isShiftsHistoryModalOpen, setIsShiftsHistoryModalOpen] = useState(false);
  const [isManualActionModalOpen, setIsManualActionModalOpen] = useState(false);
  const [isRetroSalaryModalOpen, setIsRetroSalaryModalOpen] = useState(false);
  const [selectedRetroShiftStarts, setSelectedRetroShiftStarts] = useState<string[]>([]);
  const [retroHourlySalary, setRetroHourlySalary] = useState<number | ''>('');
  const [departmentShift, setDepartmentShift] = useState<AttendanceShiftView | null>(null);
  const [manualDepartment, setManualDepartment] = useState<string | null>(null);
  const [pickerPrinters, setPickerPrinters] = useState<PrinterView[] | null>(null);
  const [isReportPreviewOpen, setIsReportPreviewOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPrintCaptureMounted, setIsPrintCaptureMounted] = useState(false);
  const [editingAction, setEditingAction] = useState<{
    id: number;
    employeeName: string;
    actionType: AttendanceActionType;
    actionDate: string;
  } | null>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const printInFlightRef = useRef(false);
  const retroSalarySaveInFlightRef = useRef(false);
  const manualSaveInFlightRef = useRef(false);
  const onShiftInFlightRef = useRef(false);
  // Bumped on every close so an on-shift reply from an earlier opening is discarded.
  const onShiftGenerationRef = useRef(0);
  useEffect(() => { if (!opened) onShiftGenerationRef.current += 1; }, [opened]);
  const [onShiftEmployees, setOnShiftEmployees] = useState<Array<PickListOption & { clockedInAt: string }> | null>(null);
  const [onShiftNow, setOnShiftNow] = useState(Date.now);
  const [isLoadingOnShift, setIsLoadingOnShift] = useState(false);
  useEffect(() => {
    if (!opened || onShiftEmployees === null) return;
    const timer = window.setInterval(() => setOnShiftNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, [opened, onShiftEmployees]);
  const onShiftOptions = onShiftEmployees?.map((employee) => ({ ...employee,
    description: formatOngoingShiftDuration(employee.clockedInAt, onShiftNow),
  })) ?? null;
  const manualDepartments = employees.find((employee) => String(employee.id) === manualEmployeeId)?.departments ?? [{ name: 'כללי', hourlySalary: null }];
  useEffect(() => {
    const assignments = employees.find((employee) => String(employee.id) === manualEmployeeId)?.departments ?? [{ name: 'כללי' }];
    setManualDepartment(assignments.length === 1 ? assignments[0]!.name : null);
  }, [manualEmployeeId, isManualActionModalOpen, employees]);

  const employeeOptions = useMemo(
    () => employees.map((employee) => ({ value: String(employee.id), label: employee.fullName })),
    [employees],
  );

  const isEmployeeLocked = lockedEmployeeId !== null;

  const selectedEmployee = useMemo(
    () => batchReport?.employee ?? employees.find((employee) => String(employee.id) === reportEmployeeId) ?? null,
    [batchReport?.employee, employees, reportEmployeeId],
  );

  const batchPrint = useAttendanceBatchPrint({
    tenantId, employeeSession, employees, range,
    enabled: !selfServiceMode && !isEmployeeLocked && !isPrinting,
    renderImage: async (report) => {
      flushSync(() => { setBatchReport(report); setIsPrintCaptureMounted(true); });
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      if (!printRef.current) throw new Error('תצוגת ההדפסה לא נטענה');
      return captureReceiptSnapshot(printRef.current);
    },
    clearReport: () => { setBatchReport(null); setIsPrintCaptureMounted(false); },
  });

  const loadDashboard = useCallback(async () => {
    if (selfServiceMode) {
      return attendanceService.getSelfDashboard(tenantId, employeeSession, { from: range.from, to: range.to });
    }
    return attendanceService.getDashboard(
      tenantId,
      employeeSession,
      { ...range, employeeId: reportEmployeeId ? Number(reportEmployeeId) : undefined },
    );
  }, [employeeSession, range, reportEmployeeId, selfServiceMode, tenantId]);

  const reload = useCallback(async (options?: { notifyIfMissingEmployee?: boolean }) => {
    if (!opened) {
      return;
    }
    if (!reportEmployeeId) {
      setDashboard(null);
      if (options?.notifyIfMissingEmployee) {
        notifications.show({ color: 'orange', title: 'נוכחות', message: 'בחר עובד לפני יצירת דוח', autoClose: 3000 });
      }
      return;
    }
    setLoading(true);
    try {
      const data = await loadDashboard();
      setDashboard(data);
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'טעינת הנוכחות נכשלה',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [loadDashboard, opened, reportEmployeeId]);

  const closedShifts = useMemo(
    () => (dashboard?.shifts ?? []).filter((shift) => !shift.isOpen),
    [dashboard?.shifts],
  );

  const reportTotals = useMemo(() => {
    const fallbackHourlyBase = selectedEmployee?.baseHourlySalary ?? closedShifts.find((shift) => typeof shift.hourlySalary === 'number')?.hourlySalary ?? 0;
    const overtime150Weekdays = new Set(attendanceReportRules.overtime150Weekdays);
    const overtime150Range = attendanceReportRules.overtime150Range;
    const overtime200SpecialDates = new Set(attendanceReportRules.overtime200SpecialDates);
    const payMinutes = buildAttendancePayMinutes(closedShifts, fallbackHourlyBase, attendanceReportRules,
      isNightWindowMinute,
      (date) => resolveDayBonusRate(date, overtime200SpecialDates, overtime150Range, overtime150Weekdays));

    type SalaryBreakdownBucket = {
      salary: number;
      departmentName: string;
      baseMinutes: number;
      basePay: number;
      overtime125Minutes: number;
      overtime125BonusPay: number;
      overtime150Minutes: number;
      overtime150BonusPay: number;
      day150Minutes: number;
      day150BonusPay: number;
      day200Minutes: number;
      day200BonusPay: number;
    };

    const salaryBuckets = new Map<string, SalaryBreakdownBucket>();
    const ensureSalaryBucket = (salary: number, departmentName: string): SalaryBreakdownBucket => {
      const key = JSON.stringify([departmentName, salary.toFixed(2)]);
      const existing = salaryBuckets.get(key);
      if (existing) {
        return existing;
      }
      const created: SalaryBreakdownBucket = {
        salary,
        departmentName,
        baseMinutes: 0,
        basePay: 0,
        overtime125Minutes: 0,
        overtime125BonusPay: 0,
        overtime150Minutes: 0,
        overtime150BonusPay: 0,
        day150Minutes: 0,
        day150BonusPay: 0,
        day200Minutes: 0,
        day200BonusPay: 0,
      };
      salaryBuckets.set(key, created);
      return created;
    };

    const computed = payMinutes.reduce(
      (acc, minute) => {
        const salaryBucket = ensureSalaryBucket(minute.salary, minute.departmentName);

        acc.totalMinutes += 1;
        acc.basePay += minute.salary / 60;
        salaryBucket.baseMinutes += 1;
        salaryBucket.basePay += minute.salary / 60;

        if (minute.overtimeBonusRate === 0.5) {
          acc.minutesOvertime150 += 1;
          acc.overtime150BonusPay += (minute.salary * 0.5) / 60;
          salaryBucket.overtime150Minutes += 1;
          salaryBucket.overtime150BonusPay += (minute.salary * 0.5) / 60;
        } else if (minute.overtimeBonusRate === 0.25) {
          acc.minutesOvertime125 += 1;
          acc.overtime125BonusPay += (minute.salary * 0.25) / 60;
          salaryBucket.overtime125Minutes += 1;
          salaryBucket.overtime125BonusPay += (minute.salary * 0.25) / 60;
        }

        if (minute.dayBonusRate === 1) {
          acc.minutesDay200 += 1;
          acc.day200BonusPay += minute.salary / 60;
          salaryBucket.day200Minutes += 1;
          salaryBucket.day200BonusPay += minute.salary / 60;
        } else if (minute.dayBonusRate === 0.5) {
          acc.minutesDay150 += 1;
          acc.day150BonusPay += (minute.salary * 0.5) / 60;
          salaryBucket.day150Minutes += 1;
          salaryBucket.day150BonusPay += (minute.salary * 0.5) / 60;
        }
        return acc;
      },
      {
        totalMinutes: 0,
        basePay: 0,
        minutesOvertime125: 0,
        overtime125BonusPay: 0,
        minutesOvertime150: 0,
        overtime150BonusPay: 0,
        minutesDay150: 0,
        day150BonusPay: 0,
        minutesDay200: 0,
        day200BonusPay: 0,
      },
    );

    const totalMinutes = computed.totalMinutes;
    const basePay = round2(computed.basePay);
    const overtime125BonusPay = round2(computed.overtime125BonusPay);
    const overtime150BonusPay = round2(computed.overtime150BonusPay);
    const day150BonusPay = round2(computed.day150BonusPay);
    const day200BonusPay = round2(computed.day200BonusPay);
    const totalPay = round2(
      basePay
      + overtime125BonusPay
      + overtime150BonusPay
      + day150BonusPay
      + day200BonusPay,
    );
    const hasAnyBonus =
      (computed.minutesOvertime125 > 0 && overtime125BonusPay > 0)
      || (computed.minutesOvertime150 > 0 && overtime150BonusPay > 0)
      || (computed.minutesDay150 > 0 && day150BonusPay > 0)
      || (computed.minutesDay200 > 0 && day200BonusPay > 0);
    const shiftSalaries = closedShifts
      .map((shift) => shift.hourlySalary)
      .filter((salary): salary is number => typeof salary === 'number');
    const uniqueSalaries = new Set(shiftSalaries.map((salary) => salary.toFixed(2)));
    const hasMixedHourlySalary = uniqueSalaries.size > 1;
    const salaryBreakdown = Array.from(salaryBuckets.values())
      .sort((a, b) => a.salary - b.salary)
      .map((bucket) => ({
        ...bucket,
        basePay: round2(bucket.basePay),
        overtime125BonusPay: round2(bucket.overtime125BonusPay),
        overtime150BonusPay: round2(bucket.overtime150BonusPay),
        day150BonusPay: round2(bucket.day150BonusPay),
        day200BonusPay: round2(bucket.day200BonusPay),
      }));
    const uniqueWorkDays = new Set(closedShifts.map((shift) => localDateKey(shift.clockInAt))).size;
    return {
      hourlyBase: fallbackHourlyBase,
      hasMixedHourlySalary,
      hasDepartmentBreakdown: salaryBreakdown.length > 0,
      hasAnyBonus,
      salaryBreakdown,
      totalMinutes,
      totalPay,
      basePay,
      overtime125BonusPay,
      overtime150BonusPay,
      day150BonusPay,
      day200BonusPay,
      minutesOvertime125: computed.minutesOvertime125,
      minutesOvertime150: computed.minutesOvertime150,
      minutesDay150: computed.minutesDay150,
      minutesDay200: computed.minutesDay200,
      workDays: uniqueWorkDays,
      shiftsCount: closedShifts.length,
    };
  }, [attendanceReportRules, closedShifts, selectedEmployee?.baseHourlySalary]);

  useEffect(() => {
    if (opened) {
      setRange(defaultRange());
    } else {
      setDashboard(null);
      setEditingAction(null);
      setIsReportModalOpen(false);
      setIsActionsHistoryModalOpen(false);
      setIsShiftsHistoryModalOpen(false);
      setIsManualActionModalOpen(false);
      setIsReportPreviewOpen(false);
      setPickerPrinters(null);
    }
  }, [opened]);

  useEffect(() => {
    if (!opened || lockedEmployeeId === null) {
      return;
    }
    const lockedEmployeeValue = String(lockedEmployeeId);
    setReportEmployeeId(lockedEmployeeValue);
    setManualEmployeeId(lockedEmployeeValue);
  }, [lockedEmployeeId, opened]);

  useEffect(() => {
    if (!opened || selfServiceMode) return;
    return attendanceService.subscribeShiftEvents(tenantId, employeeSession, (event) => {
      setDashboard((current) => current ? applyAttendanceShiftSnapshot(current, event.shift) : current);
    }, () => { void reload(); });
  }, [opened, selfServiceMode, tenantId, employeeSession, reload]);

  const handleAddManualAction = useCallback(async () => {
    if (manualSaveInFlightRef.current) return;
    if (!manualEmployeeId) {
      notifications.show({ color: 'orange', title: 'נוכחות', message: 'בחר עובד', autoClose: 3000 });
      return;
    }
    if (manualAddMode === 'single_action' && !manualActionDate) {
      notifications.show({ color: 'orange', title: 'נוכחות', message: 'בחר תאריך פעולה', autoClose: 3000 });
      return;
    }
    if (manualAddMode === 'full_shift') {
      if (!manualShiftStartDate || !manualShiftEndDate) {
        notifications.show({ color: 'orange', title: 'נוכחות', message: 'בחר תאריך התחלה וסיום למשמרת', autoClose: 3000 });
        return;
      }
      const startTs = Date.parse(manualShiftStartDate);
      const endTs = Date.parse(manualShiftEndDate);
      if (!Number.isFinite(startTs) || !Number.isFinite(endTs) || endTs <= startTs) {
        notifications.show({ color: 'orange', title: 'נוכחות', message: 'זמן הסיום חייב להיות אחרי זמן ההתחלה', autoClose: 3000 });
        return;
      }
    }

    if ((manualAddMode === 'full_shift' || manualActionType === 'clock_in') && !manualDepartment) {
      notifications.show({ color: 'orange', title: 'נוכחות', message: 'בחר מחלקה', autoClose: 3000 });
      return;
    }
    manualSaveInFlightRef.current = true;
    setSaving(true);
    try {
      if (manualAddMode === 'single_action') {
        await attendanceService.addManualAction(
          tenantId,
          {
            employeeId: Number(manualEmployeeId),
            actionType: manualActionType,
            actionDate: toDateIso(manualActionDate),
            ...(manualActionType === 'clock_in' && manualDepartment ? { departmentName: manualDepartment } : {}),
          },
          employeeSession,
        );
      } else {
        await attendanceService.addManualAction(
          tenantId,
          {
            employeeId: Number(manualEmployeeId),
            actionType: 'clock_in',
            actionDate: toDateIso(manualShiftStartDate),
            ...(manualDepartment ? { departmentName: manualDepartment } : {}),
          },
          employeeSession,
        );
        await attendanceService.addManualAction(
          tenantId,
          {
            employeeId: Number(manualEmployeeId),
            actionType: 'clock_out',
            actionDate: toDateIso(manualShiftEndDate),
          },
          employeeSession,
        );
      }
      notifications.show({
        color: 'green',
        title: 'נוכחות',
        message: 'בוצע בהצלחה',
        autoClose: 2500,
      });
      setManualActionDate('');
      setManualShiftStartDate('');
      setManualShiftEndDate('');
      await reload();
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'הוספת הפעולה נכשלה',
        autoClose: 3000,
      });
    } finally {
      manualSaveInFlightRef.current = false;
      setSaving(false);
    }
  }, [
    employeeSession,
    manualActionDate,
    manualActionType,
    manualAddMode,
    manualEmployeeId,
    manualDepartment,
    manualShiftEndDate,
    manualShiftStartDate,
    reload,
    tenantId,
  ]);

  const handleManualClose = useCallback(async (employeeId: number) => {
    setSaving(true);
    try {
      await attendanceService.manualCloseOpenShift(tenantId, employeeId, employeeSession);
      await reload();
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'סגירת המשמרת נכשלה',
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  }, [employeeSession, reload, tenantId]);

  const handleEditAction = useCallback((actionId: number, employeeName: string, currentType: AttendanceActionType, currentDate: string) => {
    setEditingAction({
      id: actionId,
      employeeName,
      actionType: currentType,
      actionDate: toDateTimeLocalValue(currentDate),
    });
  }, []);

  const handleSaveEditedAction = useCallback(async () => {
    if (!editingAction) {
      return;
    }
    if (!editingAction.actionDate) {
      notifications.show({ color: 'orange', title: 'נוכחות', message: 'יש לבחור תאריך ושעה', autoClose: 3000 });
      return;
    }
    setSaving(true);
    try {
      await attendanceService.updateAction(
        tenantId,
        editingAction.id,
        { actionType: editingAction.actionType, actionDate: toDateIso(editingAction.actionDate) },
        employeeSession,
      );
      setEditingAction(null);
      await reload();
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'עריכת הפעולה נכשלה',
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  }, [editingAction, employeeSession, reload, tenantId]);

  const printToPrinter = useCallback(async (printerId: number) => {
    if (!dashboard || printInFlightRef.current) {
      return;
    }
    printInFlightRef.current = true;
    setIsPrinting(true);
    setIsPrintCaptureMounted(true);
    try {
      // Wait one frame so the off-screen printable node is mounted before capture.
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });
      if (!printRef.current) {
        notifications.show({ color: 'red', title: 'שגיאה', message: 'תצוגת ההדפסה לא נטענה', autoClose: 3000 });
        return;
      }
      const image = await captureReceiptSnapshot(printRef.current);
      if (!image) {
        notifications.show({ color: 'red', title: 'שגיאה', message: 'לא ניתן ליצור תמונה להדפסה', autoClose: 3000 });
        return;
      }
      await attendanceService.printSlip(tenantId, printerId, image);
      notifications.show({ color: 'green', title: 'נוכחות', message: 'הדוח נשלח להדפסה', autoClose: 3000 });
      setPickerPrinters(null);
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'ההדפסה נכשלה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'שגיאה לא ידועה',
        autoClose: 3000,
      });
    } finally {
      printInFlightRef.current = false;
      setIsPrinting(false);
      setIsPrintCaptureMounted(false);
    }
  }, [dashboard, tenantId]);

  const handleProceedToPrint = useCallback(async () => {
    if (!dashboard || !reportEmployeeId) {
      notifications.show({ color: 'orange', title: 'נוכחות', message: 'בחר עובד לפני הדפסה', autoClose: 3000 });
      return;
    }
    try {
      const printers = await attendanceService.listBillablePrinters(tenantId);
      if (printers.length === 0) {
        notifications.show({ color: 'orange', title: 'נוכחות', message: 'אין מדפסות חשבונות פעילות', autoClose: 3000 });
        return;
      }
      if (printers.length === 1) {
        await printToPrinter(printers[0].id);
        return;
      }
      setPickerPrinters(printers);
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'טעינת המדפסות נכשלה',
        autoClose: 3000,
      });
    }
  }, [dashboard, printToPrinter, reportEmployeeId, tenantId]);

  const handlePrintReport = useCallback(async () => {
    if (!reportEmployeeId) {
      notifications.show({ color: 'orange', title: 'נוכחות', message: 'בחר עובד לפני הדפסה', autoClose: 3000 });
      return;
    }
    setLoading(true);
    try {
      const data = await loadDashboard();
      setDashboard(data);
      setIsReportPreviewOpen(true);
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'טעינת הנוכחות נכשלה',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [loadDashboard, reportEmployeeId]);

  const ensureEmployeeSelected = useCallback((): boolean => {
    if (reportEmployeeId) {
      return true;
    }
    notifications.show({ color: 'orange', title: 'נוכחות', message: 'בחר עובד כדי להמשיך', autoClose: 3000 });
    return false;
  }, [reportEmployeeId]);

  const openReportModal = useCallback(() => {
    if (!ensureEmployeeSelected()) {
      return;
    }
    setIsReportModalOpen(true);
    void reload();
  }, [ensureEmployeeSelected, reload]);

  const openActionsHistoryModal = useCallback(() => {
    if (!ensureEmployeeSelected()) {
      return;
    }
    setIsActionsHistoryModalOpen(true);
    void reload();
  }, [ensureEmployeeSelected, reload]);

  const openShiftsHistoryModal = useCallback(() => {
    if (!ensureEmployeeSelected()) {
      return;
    }
    setIsShiftsHistoryModalOpen(true);
    void reload();
  }, [ensureEmployeeSelected, reload]);

  const openOnShiftModal = useCallback(async () => {
    if (onShiftInFlightRef.current) return;
    onShiftInFlightRef.current = true;
    setIsLoadingOnShift(true);
    const generation = onShiftGenerationRef.current;
    try {
      const onShift = await attendanceService.getOnShift(tenantId, employeeSession);
      if (generation !== onShiftGenerationRef.current) return;
      const byName = [...onShift].sort((a, b) => compareEmployeeNames(a.employeeName, b.employeeName));
      setOnShiftNow(Date.now());
      setOnShiftEmployees(byName.map((shift) => {
        const employee = employees.find((row) => row.id === shift.employeeId);
        const photoUrl = employee && 'photoUrl' in employee ? employee.photoUrl : null;
        return { id: shift.employeeId, name: shift.employeeName, photoUrl, clockedInAt: shift.clockedInAt };
      }));
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'טעינת הנוכחות נכשלה',
        autoClose: 3000,
      });
    } finally {
      onShiftInFlightRef.current = false;
      setIsLoadingOnShift(false);
    }
  }, [employeeSession, employees, tenantId]);

  const openManualActionModal = useCallback(() => {
    if (!ensureEmployeeSelected()) {
      return;
    }
    setManualEmployeeId(reportEmployeeId);
    setIsManualActionModalOpen(true);
  }, [ensureEmployeeSelected, reportEmployeeId]);

  const openRetroSalaryModal = useCallback(async () => {
    if (!ensureEmployeeSelected()) {
      return;
    }
    setLoading(true);
    try {
      const data = await loadDashboard();
      setDashboard(data);
      setSelectedRetroShiftStarts([]);
      setRetroHourlySalary('');
      setIsRetroSalaryModalOpen(true);
    } catch (error: unknown) {
      notifications.show({ color: 'red', title: 'שגיאה', message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'טעינת הנוכחות נכשלה', autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }, [ensureEmployeeSelected, loadDashboard]);

  const selectedRetroShifts = useMemo(
    () => closedShifts.filter((shift) => selectedRetroShiftStarts.includes(shift.clockInAt)),
    [closedShifts, selectedRetroShiftStarts],
  );
  const previousRetroRates = useMemo(
    () => Array.from(new Set(selectedRetroShifts.map((shift) => shift.hourlySalary).filter((rate): rate is number => typeof rate === 'number'))).sort((a, b) => a - b),
    [selectedRetroShifts],
  );
  const currentRetroWage = round2(selectedRetroShifts.reduce((sum, shift) => sum + shift.shiftSalary, 0));
  const newRetroWage = retroHourlySalary === '' ? null : round2(selectedRetroShifts.reduce((sum, shift) => sum + (shift.durationHours * retroHourlySalary), 0));

  const saveRetroSalary = useCallback(async () => {
    if (!reportEmployeeId || retroHourlySalary === '' || selectedRetroShiftStarts.length === 0 || retroSalarySaveInFlightRef.current) {
      return;
    }
    retroSalarySaveInFlightRef.current = true;
    setSaving(true);
    try {
      await attendanceService.updateRetroSalary(tenantId, employeeSession, {
        employeeId: Number(reportEmployeeId),
        clockInAt: selectedRetroShiftStarts,
        hourlySalary: retroHourlySalary,
      });
      notifications.show({ color: 'green', title: 'נוכחות', message: 'שכר המשמרות עודכן', autoClose: 3000 });
      setIsRetroSalaryModalOpen(false);
      await reload();
    } catch (error: unknown) {
      notifications.show({ color: 'red', title: 'שגיאה', message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'עדכון השכר נכשל', autoClose: 3000 });
    } finally {
      setSaving(false);
      retroSalarySaveInFlightRef.current = false;
    }
  }, [employeeSession, reload, reportEmployeeId, retroHourlySalary, selectedRetroShiftStarts, tenantId]);

  const renderPrintableReport = (targetRef?: React.Ref<HTMLDivElement>) => {
    const hasOvertime125Section = reportTotals.minutesOvertime125 > 0 && reportTotals.overtime125BonusPay > 0;
    const hasOvertime150Section = reportTotals.minutesOvertime150 > 0 && reportTotals.overtime150BonusPay > 0;
    const hasDay150Section = reportTotals.minutesDay150 > 0 && reportTotals.day150BonusPay > 0;
    const hasDay200Section = reportTotals.minutesDay200 > 0 && reportTotals.day200BonusPay > 0;
    const summaryRows = (generalAttendanceReportRules.summaryRows ?? DEFAULT_ATTENDANCE_SUMMARY_ROWS).filter((row) => row.visible);
    const summaryValues = {
      salary: reportTotals.totalPay,
      hours: formatDurationHmsFromMinutes(reportTotals.totalMinutes),
      workDays: reportTotals.workDays,
      shifts: reportTotals.shiftsCount,
      tipsCredit: dashboard?.tipsCredit ?? 0,
      tipsCash: dashboard?.tipsCash ?? 0,
      totalPay: attendancePayableTotal(reportTotals.totalPay, dashboard?.tipsCredit ?? 0, dashboard?.tipsCash ?? 0, generalAttendanceReportRules),
    };

    return (
      <div
      ref={targetRef}
      style={{
        direction: 'rtl',
        fontFamily: 'Arial, sans-serif',
        padding: 14,
        background: '#fff',
        color: '#111',
        lineHeight: 1.3,
        overflowWrap: 'anywhere',
        width: 'min(320px, 100%)',
        maxWidth: '100%',
        boxSizing: 'border-box',
        margin: '0 auto',
      }}
    >
      <div style={{ textAlign: 'center', fontWeight: 700, marginBottom: 6, fontSize: 19 }}>דוח נוכחות עובד</div>
      <div style={{ textAlign: 'center', marginBottom: 8, fontSize: 12 }}>
        <span>מ־</span>
        <span dir="ltr">{formatRangeDateShort((batchReport?.range ?? range).from)}</span>
        <span> עד </span>
        <span dir="ltr">{formatRangeDateShort((batchReport?.range ?? range).to)}</span>
      </div>
      <div style={{ borderTop: '1px solid #222', borderBottom: '1px solid #222', padding: '6px 0', marginBottom: 8, fontWeight: 700, fontSize: 15 }}>
        עובד: {selectedEmployee?.fullName ?? '—'}
      </div>
      <div style={{ borderBottom: '1px dashed #bbb', marginBottom: 8, paddingBottom: 8, fontSize: 11 }}>
        <div style={{ marginBottom: 2 }}>
          כללים: רגיל עד {attendanceReportRules.overtime125StartHours}ש, 125% עד {attendanceReportRules.overtime150StartHours}ש, לאחר מכן 150%
        </div>
        <div style={{ marginBottom: 2 }}>
          משמרת לילה: {attendanceReportRules.useNightShiftOvertimeRules
            ? 'פעיל (22:00-06:00, לפחות 2 שעות בחלון; 125% מתחיל שעה מוקדם יותר)'
            : 'כבוי'}
        </div>
        <div style={{ marginBottom: 2 }}>
          טווח 150%: {attendanceReportRules.overtime150Range
            ? `${weekdayLabel(attendanceReportRules.overtime150Range.fromWeekday)} ${attendanceReportRules.overtime150Range.fromTime} עד ${weekdayLabel(attendanceReportRules.overtime150Range.toWeekday)} ${attendanceReportRules.overtime150Range.toTime}`
            : (attendanceReportRules.overtime150Weekdays.length > 0
              ? attendanceReportRules.overtime150Weekdays.map((day) => weekdayLabel(day)).join(', ')
              : 'אין')}
        </div>
        <div>
          ימים מיוחדים 200%: {attendanceReportRules.overtime200SpecialDates.length > 0
            ? attendanceReportRules.overtime200SpecialDates.map((date) => formatRangeDateShort(date)).join(', ')
            : 'אין'}
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 8 }}>
        <thead>
          <tr>
            <th style={{ borderBottom: '1px solid #333', padding: '4px 2px' }}>יום</th>
            <th style={{ borderBottom: '1px solid #333', padding: '4px 2px' }}>מחלקה</th>
            <th style={{ borderBottom: '1px solid #333', padding: '4px 2px' }}>תאריך</th>
            <th style={{ borderBottom: '1px solid #333', padding: '4px 2px' }}>כניסה</th>
            <th style={{ borderBottom: '1px solid #333', padding: '4px 2px' }}>יציאה</th>
            <th style={{ borderBottom: '1px solid #333', padding: '4px 2px' }}>סה"כ</th>
          </tr>
        </thead>
        <tbody>
          {closedShifts.map((shift, index) => (
            <tr key={`${shift.employeeId}-print-${index}`}>
              <td style={{ borderBottom: '1px dashed #bbb', padding: '3px 2px' }}>{hebrewWeekday(shift.clockInAt)}</td>
              <td style={{ borderBottom: '1px dashed #bbb', padding: '3px 2px', maxWidth: 48, overflowWrap: 'anywhere' }}>{shift.departmentName ?? 'כללי'}</td>
              <td style={{ borderBottom: '1px dashed #bbb', padding: '3px 2px' }} dir="ltr">{formatDateCompact(shift.clockInAt)}</td>
              <td style={{ borderBottom: '1px dashed #bbb', padding: '3px 2px' }} dir="ltr">{formatTimeCompact(shift.clockInAt)}</td>
              <td style={{ borderBottom: '1px dashed #bbb', padding: '3px 2px' }} dir="ltr">{formatTimeCompact(shift.clockOutAt)}</td>
              <td style={{ borderBottom: '1px dashed #bbb', padding: '3px 2px' }} dir="ltr">{formatDurationHmsFromMinutes(shift.durationMinutes)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ borderTop: '2px double #222', marginTop: 2, paddingTop: 7 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
          <span>שכר בסיס (100%)</span>
          {!reportTotals.hasDepartmentBreakdown && (
            <span dir="ltr">{`${formatDurationHmsFromMinutes(reportTotals.totalMinutes)} x ₪ ${reportTotals.hourlyBase} = ₪ ${reportTotals.basePay}`}</span>
          )}
        </div>
        {reportTotals.hasDepartmentBreakdown
          ? reportTotals.salaryBreakdown.map((bucket) => (
            <div key={`base-${bucket.departmentName}-${bucket.salary}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, paddingInlineStart: 10 }}>
              <span>{bucket.departmentName} · ₪ {bucket.salary}</span>
              <span dir="ltr">{`${formatDurationHmsFromMinutes(bucket.baseMinutes)} x ₪ ${bucket.salary} = ₪ ${bucket.basePay}`}</span>
            </div>
          ))
          : null}

        {reportTotals.hasAnyBonus && (
          <div style={{ borderTop: '1px dashed #bbb', margin: '7px 0' }} />
        )}

        {hasOvertime125Section && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span>בונוס שעות נוספות (+25%)</span>
            {!reportTotals.hasDepartmentBreakdown && (
              <span dir="ltr">{`${formatDurationHmsFromMinutes(reportTotals.minutesOvertime125)} x ₪ ${round2(reportTotals.hourlyBase * 0.25)} = ₪ ${reportTotals.overtime125BonusPay}`}</span>
            )}
          </div>
        )}
        {reportTotals.hasDepartmentBreakdown
          ? reportTotals.salaryBreakdown
            .filter((bucket) => bucket.overtime125Minutes > 0 && bucket.overtime125BonusPay > 0)
            .map((bucket) => (
              <div key={`ot125-${bucket.departmentName}-${bucket.salary}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, paddingInlineStart: 10 }}>
                <span>{bucket.departmentName}</span>
                <span dir="ltr">{`${formatDurationHmsFromMinutes(bucket.overtime125Minutes)} x ₪ ${round2(bucket.salary * 0.25)} = ₪ ${bucket.overtime125BonusPay}`}</span>
              </div>
            ))
          : null}
        {hasOvertime125Section && (hasOvertime150Section || hasDay150Section || hasDay200Section) && (
          <div style={{ borderTop: '1px dashed #ddd', margin: '6px 0' }} />
        )}

        {hasOvertime150Section && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span>בונוס שעות נוספות (+50%)</span>
            {!reportTotals.hasDepartmentBreakdown && (
              <span dir="ltr">{`${formatDurationHmsFromMinutes(reportTotals.minutesOvertime150)} x ₪ ${round2(reportTotals.hourlyBase * 0.5)} = ₪ ${reportTotals.overtime150BonusPay}`}</span>
            )}
          </div>
        )}
        {reportTotals.hasDepartmentBreakdown
          ? reportTotals.salaryBreakdown
            .filter((bucket) => bucket.overtime150Minutes > 0 && bucket.overtime150BonusPay > 0)
            .map((bucket) => (
              <div key={`ot150-${bucket.departmentName}-${bucket.salary}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, paddingInlineStart: 10 }}>
                <span>{bucket.departmentName}</span>
                <span dir="ltr">{`${formatDurationHmsFromMinutes(bucket.overtime150Minutes)} x ₪ ${round2(bucket.salary * 0.5)} = ₪ ${bucket.overtime150BonusPay}`}</span>
              </div>
            ))
          : null}
        {hasOvertime150Section && (hasDay150Section || hasDay200Section) && (
          <div style={{ borderTop: '1px dashed #ddd', margin: '6px 0' }} />
        )}

        {hasDay150Section && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span>בונוס יום 150% (+50%)</span>
            {!reportTotals.hasDepartmentBreakdown && (
              <span dir="ltr">{`${formatDurationHmsFromMinutes(reportTotals.minutesDay150)} x ₪ ${round2(reportTotals.hourlyBase * 0.5)} = ₪ ${reportTotals.day150BonusPay}`}</span>
            )}
          </div>
        )}
        {reportTotals.hasDepartmentBreakdown
          ? reportTotals.salaryBreakdown
            .filter((bucket) => bucket.day150Minutes > 0 && bucket.day150BonusPay > 0)
            .map((bucket) => (
              <div key={`day150-${bucket.departmentName}-${bucket.salary}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, paddingInlineStart: 10 }}>
                <span>{bucket.departmentName}</span>
                <span dir="ltr">{`${formatDurationHmsFromMinutes(bucket.day150Minutes)} x ₪ ${round2(bucket.salary * 0.5)} = ₪ ${bucket.day150BonusPay}`}</span>
              </div>
            ))
          : null}
        {hasDay150Section && hasDay200Section && (
          <div style={{ borderTop: '1px dashed #ddd', margin: '6px 0' }} />
        )}

        {hasDay200Section && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span>בונוס יום 200% (+100%)</span>
            {!reportTotals.hasDepartmentBreakdown && (
              <span dir="ltr">{`${formatDurationHmsFromMinutes(reportTotals.minutesDay200)} x ₪ ${round2(reportTotals.hourlyBase * 1)} = ₪ ${reportTotals.day200BonusPay}`}</span>
            )}
          </div>
        )}
        {reportTotals.hasDepartmentBreakdown
          ? reportTotals.salaryBreakdown
            .filter((bucket) => bucket.day200Minutes > 0 && bucket.day200BonusPay > 0)
            .map((bucket) => (
              <div key={`day200-${bucket.departmentName}-${bucket.salary}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, paddingInlineStart: 10 }}>
                <span>{bucket.departmentName}</span>
                <span dir="ltr">{`${formatDurationHmsFromMinutes(bucket.day200Minutes)} x ₪ ${round2(bucket.salary * 1)} = ₪ ${bucket.day200BonusPay}`}</span>
              </div>
            ))
          : null}
        {reportTotals.hasAnyBonus && (
          <div style={{ borderTop: '1px dashed #bbb', margin: '7px 0' }} />
        )}
        {summaryRows.map((row, index) => {
          const statistic = ['hours', 'workDays', 'shifts'].includes(row.id);
          const previousStatistic = index > 0 && ['hours', 'workDays', 'shifts'].includes(summaryRows[index - 1]!.id);
          return statistic
            ? <div key={row.id} data-testid={`attendance-summary-output-${row.id}`} style={{ marginTop: previousStatistic ? 3 : 10, fontSize: 13, textAlign: 'center' }}>{row.label}: <span dir="ltr">{summaryValues[row.id]}</span></div>
            : <div key={row.id} data-testid={`attendance-summary-output-${row.id}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: row.id === 'salary' || row.id === 'totalPay' ? 17 : 13, fontWeight: row.id === 'salary' || row.id === 'totalPay' ? 700 : 400, marginTop: 8 }}>
              <span>{row.label}</span><span dir="ltr">₪ {summaryValues[row.id]}</span>
            </div>;
        })}
      </div>

      <div style={{ textAlign: 'center', fontSize: 11, color: '#666', marginTop: 14 }}>
        Powered by COMMIT-AI
      </div>
    </div>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={() => { if (!batchPrint.busy && !isPrinting) { batchPrint.cancel(); setIsMultiEmployeePickerOpen(false); setOnShiftEmployees(null); onClose(); } }}
      closeOnEscape={!batchPrint.busy && !isPrinting}
      closeOnClickOutside={!batchPrint.busy && !isPrinting}
      title="נוכחות"
      size="xl"
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
      closeButtonProps={{ ...{ 'data-testid': 'settings-attendance-close' }, disabled: batchPrint.busy || isPrinting }}
    >
      <section className={settingsClasses.section} dir="rtl">
        {selfServiceMode ? (
          <>
            <h3 className={settingsClasses.sectionHeading}>דוח נוכחות אישי</h3>
            <p className={settingsClasses.sectionSubheading}>כאן ניתן לצפות ולהדפיס את דוח הנוכחות האישי שלך.</p>
            <div className={settingsClasses.formRow}>
              <TextInput
                data-testid="attendance-range-from-self"
                type="date"
                label="מתאריך"
                value={range.from}
                onChange={(event) => setRange((previous) => ({ ...previous, from: event.currentTarget.value }))}
              />
              <TextInput
                data-testid="attendance-range-to-self"
                type="date"
                label="עד תאריך"
                value={range.to}
                onChange={(event) => setRange((previous) => ({ ...previous, to: event.currentTarget.value }))}
              />
            </div>
            <div className={settingsClasses.formActions}>
              <Button
                variant="save"
                onClick={() => { void handlePrintReport(); }}
                loading={loading}
                disabled={!reportEmployeeId}
                dataTestId="attendance-print-self"
              >
                תצוגה מקדימה והדפסה
              </Button>
            </div>
          </>
        ) : (
          <>
            <h3 className={settingsClasses.sectionHeading}>בחר עובד</h3>
            <p className={settingsClasses.sectionSubheading}>לאחר בחירת עובד תוכל לבחור פעולה.</p>
            <div className={settingsClasses.formRow}>
              <TextInput
                data-testid="attendance-range-from"
                type="date"
                label="מתאריך"
                value={range.from}
                disabled={batchPrint.busy || isPrinting}
                onChange={(event) => setRange((previous) => ({ ...previous, from: event.currentTarget.value }))}
              />
              <TextInput
                data-testid="attendance-range-to"
                type="date"
                label="עד תאריך"
                value={range.to}
                disabled={batchPrint.busy || isPrinting}
                onChange={(event) => setRange((previous) => ({ ...previous, to: event.currentTarget.value }))}
              />
            </div>
            <div className={settingsClasses.formRow}>
              <Select
                data-testid="attendance-report-employee"
                label="עובד"
                placeholder="בחר עובד"
                data={isEmployeeLocked ? employeeOptions : [{ value: '__all__', label: 'כל העובדים' }, { value: '__multiple__', label: 'בחירה מרובה של עובדים' }, ...employeeOptions]}
                value={reportEmployeeId}
                onChange={(value) => {
                  if (isEmployeeLocked) {
                    return;
                  }
                  if (value === '__all__') { batchPrint.begin(employees.map((employee) => employee.id)); return; }
                  if (value === '__multiple__') { setIsMultiEmployeePickerOpen(true); return; }
                  setReportEmployeeId(value);
                  setDashboard(null);
                }}
                searchable
                disabled={isEmployeeLocked || batchPrint.busy || isPrinting}
              />
            </div>
          </>
        )}

          {!selfServiceMode && (
            <div className={settingsClasses.formRow}>
              <Button variant="save" onClick={openReportModal} disabled={!reportEmployeeId} dataTestId="attendance-open-report-modal">דוח נוכחות</Button>
              <Button variant="save" onClick={openActionsHistoryModal} disabled={!reportEmployeeId} dataTestId="attendance-open-actions-history-modal">היסטוריית פעולות</Button>
              <Button variant="save" onClick={openShiftsHistoryModal} disabled={!reportEmployeeId} dataTestId="attendance-open-shifts-history-modal">היסטוריית משמרות</Button>
              <Button variant="save" onClick={() => { void openRetroSalaryModal(); }} disabled={!reportEmployeeId || loading} dataTestId="attendance-open-retro-salary-modal">עדכן שכר משמרת קודמת</Button>
              <Button variant="save" onClick={openManualActionModal} disabled={!reportEmployeeId} dataTestId="attendance-open-manual-action-modal">הוספת פעולה ידנית</Button>
              <Button variant="save" onClick={() => { void openOnShiftModal(); }} loading={isLoadingOnShift} dataTestId="attendance-open-on-shift-modal">מי במשמרת</Button>
              <Button variant="save" onClick={() => setIsQrOpen(true)} dataTestId="attendance-open-qr">QR לנוכחות</Button>
              {onSaveReportRules && <Button variant="save" onClick={() => setIsSummaryOpen(true)} disabled={batchPrint.busy || isPrinting} dataTestId="attendance-open-summary">עריכת סיכום דוח</Button>}
            </div>
          )}

          <div className={settingsClasses.lead}>
            עובד נבחר: {selectedEmployee?.fullName ?? '—'}
          </div>
        </section>

        <AttendanceQrModal opened={opened && !selfServiceMode && isQrOpen} onClose={() => setIsQrOpen(false)} tenantId={tenantId} employeeSession={employeeSession} />
        {onSaveReportRules && <AttendanceSummaryModal opened={opened && !selfServiceMode && isSummaryOpen} onClose={() => setIsSummaryOpen(false)} rules={generalAttendanceReportRules} onSave={onSaveReportRules} />}

        <Modal
          opened={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          title="דוח נוכחות"
          centered
          size="lg"
          radius="md"
          classNames={{ root: navyModal.root }}
        >
          <section className={settingsClasses.section} dir="rtl">
            <div className={settingsClasses.formActions}>
              <Button variant="save" onClick={() => { void handlePrintReport(); }} loading={loading} disabled={!reportEmployeeId} dataTestId="attendance-print">תצוגה מקדימה והדפסה</Button>
            </div>

            <div className={settingsClasses.lead}>
              עובד: {selectedEmployee?.fullName ?? '—'} | סה"כ שעות: {dashboard?.totalHours ?? 0} | סה"כ שכר: {dashboard?.totalSalary ?? 0}
            </div>
          </section>
        </Modal>

        <Modal
          opened={isRetroSalaryModalOpen}
          onClose={() => { if (!saving) setIsRetroSalaryModalOpen(false); }}
          title="עדכון שכר משמרת קודמת"
          centered
          size="lg"
          radius="md"
          classNames={{ root: navyModal.root }}
        >
          <section className={settingsClasses.section} dir="rtl">
            <p className={settingsClasses.sectionSubheading}>בחר משמרת אחת או יותר לעדכון שכר שעתי.</p>
            <div className={settingsClasses.formRow}>
              <TextInput
                data-testid="attendance-retro-salary-range-from"
                type="date"
                label="מתאריך"
                value={range.from}
                onChange={(event) => setRange((previous) => ({ ...previous, from: event.currentTarget.value }))}
              />
              <TextInput
                data-testid="attendance-retro-salary-range-to"
                type="date"
                label="עד תאריך"
                value={range.to}
                onChange={(event) => setRange((previous) => ({ ...previous, to: event.currentTarget.value }))}
              />
            </div>
            <div className={settingsClasses.formActions}>
              <Button
                variant="save"
                onClick={() => { void reload({ notifyIfMissingEmployee: true }); }}
                loading={loading}
                disabled={!reportEmployeeId}
                dataTestId="attendance-retro-salary-refresh"
              >
                רענן משמרות
              </Button>
            </div>
            <div className={settingsClasses.lead}>שכר קודם: {previousRetroRates.length > 0 ? previousRetroRates.join(', ') : '—'} | שכר מחושב נוכחי: {currentRetroWage}</div>
            <div className={settingsClasses.tableWrap}>
              <table className={settingsClasses.table}>
                <thead><tr><th>בחר</th><th>כניסה</th><th>יציאה</th><th>שכר לשעה</th><th>שכר משמרת</th></tr></thead>
                <tbody>
                  {closedShifts.map((shift) => (
                    <tr key={shift.clockInAt}>
                      <td><Checkbox checked={selectedRetroShiftStarts.includes(shift.clockInAt)} onChange={(event) => setSelectedRetroShiftStarts((previous) => event.currentTarget.checked ? [...previous, shift.clockInAt] : previous.filter((value) => value !== shift.clockInAt))} aria-label={`בחר משמרת ${shift.clockInAt}`} /></td>
                      <td>{formatDateTimeWithWeekday(shift.clockInAt)}</td><td>{formatDateTimeWithWeekday(shift.clockOutAt)}</td><td>{shift.hourlySalary ?? '—'}</td><td>{shift.shiftSalary}</td>
                    </tr>
                  ))}
                  {closedShifts.length === 0 && <tr><td colSpan={5} className={settingsClasses.empty}>אין משמרות סגורות לבחירה.</td></tr>}
                </tbody>
              </table>
            </div>
            <NumberInput label="שכר שעתי חדש" placeholder="לדוגמה: 45" value={retroHourlySalary} onChange={(value) => setRetroHourlySalary(typeof value === 'number' ? value : '')} min={0} decimalScale={2} radius="md" data-testid="attendance-retro-salary-input" />
            <div className={settingsClasses.lead}>שכר מחושב חדש: {newRetroWage ?? '—'}</div>
            <div className={settingsClasses.formActions}><Button variant="save" onClick={() => { void saveRetroSalary(); }} loading={saving} disabled={selectedRetroShiftStarts.length === 0 || retroHourlySalary === ''} dataTestId="attendance-retro-salary-confirm">אשר</Button></div>
          </section>
        </Modal>

        <Modal
          opened={isActionsHistoryModalOpen}
          onClose={() => {
            if (!saving) {
              setIsActionsHistoryModalOpen(false);
            }
          }}
          title="היסטוריית פעולות"
          centered
          size="xl"
          radius="md"
          classNames={{ root: navyModal.root }}
        >
          <section className={settingsClasses.section} dir="rtl">
            <div className={settingsClasses.formRow}>
              <TextInput
                data-testid="attendance-actions-range-from"
                type="date"
                label="מתאריך"
                value={range.from}
                onChange={(event) => setRange((previous) => ({ ...previous, from: event.currentTarget.value }))}
              />
              <TextInput
                data-testid="attendance-actions-range-to"
                type="date"
                label="עד תאריך"
                value={range.to}
                onChange={(event) => setRange((previous) => ({ ...previous, to: event.currentTarget.value }))}
              />
            </div>
            <div className={settingsClasses.formActions}>
              <Button variant="save" onClick={() => { void reload({ notifyIfMissingEmployee: true }); }} loading={loading} disabled={!reportEmployeeId} dataTestId="attendance-actions-refresh">רענן</Button>
            </div>

            <div className={settingsClasses.tableWrap}>
              <table className={settingsClasses.table}>
                <thead>
                  <tr>
                    <th>עובד</th>
                    <th>סוג פעולה</th>
                    <th>תאריך</th>
                    <th>פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboard?.actions ?? []).map((action) => (
                    <tr key={action.id}>
                      <td>{action.employeeName}</td>
                      <td>{formatActionType(action.actionType)}</td>
                      <td>{formatDateTimeWithWeekday(action.actionDate)}</td>
                      <td className={settingsClasses.actionsCell}>
                        <Button
                          size="sm"
                          variant="save"
                          dataTestId={`attendance-edit-action-${action.id}`}
                          onClick={() => { void handleEditAction(action.id, action.employeeName, action.actionType, action.actionDate); }}
                          disabled={saving}
                        >
                          ערוך
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {dashboard && dashboard.actions.length === 0 && (
                    <tr>
                      <td colSpan={4} className={settingsClasses.empty}>אין פעולות בטווח שנבחר.</td>
                    </tr>
                  )}
                  {!dashboard && (
                    <tr>
                      <td colSpan={4} className={settingsClasses.empty}>בחר טווח תאריכים ולחץ רענן.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </Modal>

        <Modal
          opened={isShiftsHistoryModalOpen}
          onClose={() => {
            if (!saving) {
              setIsShiftsHistoryModalOpen(false);
            }
          }}
          title="היסטוריית משמרות"
          centered
          size="xl"
          radius="md"
          classNames={{ root: navyModal.root }}
        >
          <section className={settingsClasses.section} dir="rtl">
            <div className={settingsClasses.formRow}>
              <TextInput
                data-testid="attendance-shifts-range-from"
                type="date"
                label="מתאריך"
                value={range.from}
                onChange={(event) => setRange((previous) => ({ ...previous, from: event.currentTarget.value }))}
              />
              <TextInput
                data-testid="attendance-shifts-range-to"
                type="date"
                label="עד תאריך"
                value={range.to}
                onChange={(event) => setRange((previous) => ({ ...previous, to: event.currentTarget.value }))}
              />
            </div>
            <div className={settingsClasses.formActions}>
              <Button variant="save" onClick={() => { void reload({ notifyIfMissingEmployee: true }); }} loading={loading} disabled={!reportEmployeeId} dataTestId="attendance-shifts-refresh">רענן</Button>
            </div>

            <div className={settingsClasses.tableWrap}>
              <table className={settingsClasses.table}>
                <thead>
                  <tr>
                    <th>עובד</th>
                    <th>כניסה</th>
                    <th>מחלקה</th>
                    <th>יציאה</th>
                    <th>משך (שעות)</th>
                    <th>שכר לשעה</th>
                    <th>שכר משמרת</th>
                    <th>סטטוס</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboard?.shifts ?? []).map((shift, index) => (
                    <tr key={`${shift.employeeId}-${shift.clockInAt}-${index}`}>
                      <td>{shift.employeeName}</td>
                      <td>{formatDateTimeWithWeekday(shift.clockInAt)}</td>
                      <td>{shift.departmentName ?? 'כללי'}</td>
                      <td>{formatDateTimeWithWeekday(shift.clockOutAt)}</td>
                      <td>{shift.durationHours}</td>
                      <td>{shift.hourlySalary ?? '—'}</td>
                      <td>{shift.shiftSalary}</td>
                      <td>{shift.isOpen ? 'פתוחה' : 'סגורה'} {!selfServiceMode && <Button size="sm" variant="save" onClick={() => setDepartmentShift(shift)}
                        disabled={!shift.clockInActionId || !shift.updatedAt} dataTestId={`attendance-department-edit-${shift.clockInActionId}`}>עדכן מחלקה</Button>}</td>
                    </tr>
                  ))}
                  {dashboard && dashboard.shifts.length === 0 && (
                    <tr>
                      <td colSpan={8} className={settingsClasses.empty}>אין משמרות בטווח שנבחר.</td>
                    </tr>
                  )}
                  {!dashboard && (
                    <tr>
                      <td colSpan={8} className={settingsClasses.empty}>בחר טווח תאריכים ולחץ רענן.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </Modal>

        <Modal
          opened={isManualActionModalOpen}
          onClose={() => {
            if (!saving) {
              setIsManualActionModalOpen(false);
            }
          }}
          title="הוספת פעולה ידנית"
          centered
          size="lg"
          radius="md"
          classNames={{ root: navyModal.root }}
          closeButtonProps={{ 'data-testid': 'attendance-manual-close' } as Record<string, string>}
        >
          <section className={settingsClasses.section} dir="rtl">
            <div className={settingsClasses.formRow}>
              <Select
                data-testid="attendance-manual-employee"
                label="עובד"
                placeholder="בחר עובד"
                data={employeeOptions}
                value={manualEmployeeId}
                onChange={setManualEmployeeId}
              />
              {(manualAddMode === 'full_shift' || manualActionType === 'clock_in') && manualDepartments.length > 1 && <Select label="מחלקה"
                data={manualDepartments.map((department) => department.name)} value={manualDepartment} onChange={setManualDepartment} data-testid="attendance-manual-department" />}
              <Select
                data-testid="attendance-manual-mode"
                label="מצב הוספה"
                data={[
                  { value: 'single_action', label: 'פעולה בודדת' },
                  { value: 'full_shift', label: 'משמרת מלאה (כניסה + יציאה)' },
                ]}
                value={manualAddMode}
                onChange={(value) => setManualAddMode((value ?? 'single_action') as ManualAddMode)}
              />
              {manualAddMode === 'single_action' ? (
                <>
                  <Select
                    data-testid="attendance-manual-type"
                    label="סוג פעולה"
                    data={[
                      { value: 'clock_in', label: 'כניסה למשמרת' },
                      { value: 'clock_out', label: 'יציאה ממשמרת' },
                    ]}
                    value={manualActionType}
                    onChange={(value) => setManualActionType((value ?? 'clock_in') as AttendanceActionType)}
                  />
                  <TextInput
                    data-testid="attendance-manual-date"
                    type="datetime-local"
                    label="תאריך ושעה"
                    value={manualActionDate}
                    onChange={(event) => setManualActionDate(event.currentTarget.value)}
                  />
                </>
              ) : (
                <>
                  <TextInput
                    data-testid="attendance-manual-shift-start"
                    type="datetime-local"
                    label="התחלת משמרת"
                    value={manualShiftStartDate}
                    onChange={(event) => setManualShiftStartDate(event.currentTarget.value)}
                  />
                  <TextInput
                    data-testid="attendance-manual-shift-end"
                    type="datetime-local"
                    label="סיום משמרת"
                    value={manualShiftEndDate}
                    onChange={(event) => setManualShiftEndDate(event.currentTarget.value)}
                  />
                </>
              )}
            </div>
            <div className={settingsClasses.formActions}>
              <Button variant="save" onClick={() => { void handleAddManualAction(); }} loading={saving} dataTestId="attendance-manual-add">
                {manualAddMode === 'single_action' ? 'הוסף פעולה ידנית' : 'הוסף משמרת מלאה'}
              </Button>
            </div>
          </section>
        </Modal>

      {isPrintCaptureMounted && (
        <div style={{ position: 'fixed', left: -10000, top: 0, width: 360 }} aria-hidden="true">
          {renderPrintableReport(printRef)}
        </div>
      )}

      <AttendanceDepartmentModal shift={departmentShift} tenantId={tenantId} employeeSession={employeeSession}
        departments={employees.find((employee) => employee.id === departmentShift?.employeeId)?.departments ?? [{ name: 'כללי', hourlySalary: selectedEmployee?.baseHourlySalary ?? null }]}
        onClose={() => setDepartmentShift(null)} />

      <Modal
        opened={editingAction !== null}
        onClose={() => {
          if (!saving) {
            setEditingAction(null);
          }
        }}
        title="עריכת פעולה"
        centered
        size="md"
        radius="md"
        classNames={{ root: navyModal.root }}
        closeOnClickOutside={!saving}
        closeOnEscape={!saving}
      >
        <section className={settingsClasses.section} dir="rtl">
          <div className={settingsClasses.lead}>עובד: {editingAction?.employeeName ?? '—'}</div>
          <div className={settingsClasses.formRow}>
            <Select
              data-testid="attendance-edit-type"
              label="סוג פעולה"
              data={[
                { value: 'clock_in', label: 'כניסה למשמרת' },
                { value: 'clock_out', label: 'יציאה ממשמרת' },
              ]}
              value={editingAction?.actionType ?? null}
              onChange={(value) => {
                if (!value) {
                  return;
                }
                setEditingAction((previous) => {
                  if (!previous) {
                    return previous;
                  }
                  return { ...previous, actionType: value as AttendanceActionType };
                });
              }}
            />
            <TextInput
              data-testid="attendance-edit-date"
              type="datetime-local"
              label="תאריך ושעה"
              value={editingAction?.actionDate ?? ''}
              onChange={(event) => {
                const nextValue = event.currentTarget.value;
                setEditingAction((previous) => {
                  if (!previous) {
                    return previous;
                  }
                  return { ...previous, actionDate: nextValue };
                });
              }}
            />
          </div>
          <div className={settingsClasses.formActions}>
            <Button variant="cancel" onClick={() => setEditingAction(null)} disabled={saving} dataTestId="attendance-edit-cancel">ביטול</Button>
            <Button variant="save" onClick={() => { void handleSaveEditedAction(); }} loading={saving} dataTestId="attendance-edit-save">שמור שינויים</Button>
          </div>
        </section>
      </Modal>

      <Modal
        opened={isReportPreviewOpen}
        onClose={() => setIsReportPreviewOpen(false)}
        title="תצוגה מקדימה - דוח נוכחות"
        centered
        size="lg"
        radius="md"
        classNames={{ root: navyModal.root }}
      >
        <div style={{ width: '100%', overflowX: 'auto', borderRadius: 8, background: '#f5f5f5', padding: 12 }}>
          <div style={{ minWidth: 0, display: 'flex', justifyContent: 'center' }}>
            {renderPrintableReport()}
          </div>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button
            variant="cancel"
            onClick={() => setIsReportPreviewOpen(false)}
            dataTestId="attendance-preview-close"
          >
            סגור בלי להדפיס
          </Button>
          <Button
            variant="save"
            onClick={() => {
              setIsReportPreviewOpen(false);
              void handleProceedToPrint();
            }}
            loading={isPrinting}
            dataTestId="attendance-preview-print"
          >
            המשך להדפסה
          </Button>
        </div>
      </Modal>

      <PickListModal
        title="בחר מדפסת"
        options={(batchPrint.printers ?? pickerPrinters)?.map((printer) => ({ id: printer.id, name: printer.name })) ?? null}
        disabled={isPrinting || batchPrint.busy}
        dataTestId="attendance-printer-pick"
        optionColor="primary"
        onPick={(printerId) => { if (batchPrint.printers) void batchPrint.print(printerId); else void printToPrinter(printerId); }}
        onClose={() => { batchPrint.cancel(); setPickerPrinters(null); }}
      />
      <PickListModal
        title="בחירה מרובה של עובדים"
        options={isMultiEmployeePickerOpen ? employees.map((employee) => ({ id: employee.id, name: employee.fullName, photoUrl: 'photoUrl' in employee ? employee.photoUrl : null })) : null}
        variant="employee-grid"
        dataTestId="attendance-batch-employees"
        selectedIds={selectedBatchEmployeeIds}
        onPick={(id) => setSelectedBatchEmployeeIds((previous) => previous.includes(id) ? previous.filter((selected) => selected !== id) : [...previous, id])}
        onConfirm={() => { setIsMultiEmployeePickerOpen(false); batchPrint.begin(selectedBatchEmployeeIds); }}
        onClose={() => setIsMultiEmployeePickerOpen(false)}
      />
      <PickListModal
        title="מי במשמרת"
        options={onShiftOptions}
        size="xl"
        variant="employee-grid"
        optionColor="primary"
        dataTestId="attendance-on-shift"
        onClose={() => setOnShiftEmployees(null)}
      />
      <NoBillWarningModal opened={batchPrint.confirmationOpen} onClose={batchPrint.cancel} onConfirm={() => { void batchPrint.print(); }}
        message="אתה עומד להדפיס אחד אחד את כלל הדוחות נוכחות של העובדים" hint="האם אתה בטוח?" confirmLabel="הדפס" confirmTestId="attendance-batch-confirm" zIndex={1300} />
      <Modal opened={batchPrint.progress !== null || batchPrint.completedTotal !== null} onClose={() => {}}
        centered size="sm" zIndex={1300} withCloseButton={false} closeOnEscape={false} closeOnClickOutside={false} dir="rtl">
        <div className={settingsClasses.batchPrintStatus} data-testid="attendance-batch-progress">
          <Modal.Title className={settingsClasses.batchPrintTitle} aria-live="polite" data-testid="attendance-batch-title">
            {batchPrint.completedTotal === null && <span>מדפיס</span>}
            <bdi dir="ltr" className={settingsClasses.batchPrintCounts}>
              <span className={settingsClasses.batchPrintCurrent} data-complete={batchPrint.completedTotal !== null || undefined} data-testid="attendance-batch-current">{batchPrint.completedTotal ?? batchPrint.progress?.current ?? 0}</span>
              /<span data-testid="attendance-batch-total">{batchPrint.completedTotal ?? batchPrint.progress?.total ?? 0}</span>
            </bdi>
            {batchPrint.completedTotal !== null && <span>הודפס בהצלחה</span>}
          </Modal.Title>
          <Progress color="green" size="sm" radius="xl" aria-label="התקדמות הדפסת דוחות נוכחות" data-testid="attendance-batch-progress-bar"
            value={batchPrint.completedTotal !== null ? 100 : batchPrint.progress ? batchPrint.progress.completed / batchPrint.progress.total * 100 : 0}
            classNames={{ root: settingsClasses.batchPrintTrack, section: settingsClasses.batchPrintFill }} />
          {batchPrint.completedTotal !== null && <Button variant="confirm" onClick={batchPrint.acknowledge} fullWidth dataTestId="attendance-batch-acknowledge">אישור</Button>}
        </div>
      </Modal>
    </Modal>
  );
};
